// utils/api.js
// API請求工具函數
import config from '../config';
const API_BASE_URL = `${config.server}/api`;


// 獲取存儲的token
export const getAuthToken = () => {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

// 移除token（登出時使用）
export const removeAuthToken = () => {
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token');
};

// 帶認證的API請求
export const authenticatedRequest = async (url, options = {}) => {
  const token = getAuthToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${url}`, config);
  
  // 如果token過期或無效，自動跳轉到登錄頁
  if (response.status === 401) {
    removeAuthToken();
    window.location.href = '/signin';
    return;
  }

  return response;
};

// 獲取用戶資料
export const getUserProfile = async () => {
  try {
    const response = await authenticatedRequest('/profile');
    if (response.ok) {
      return await response.json();
    }
    throw new Error('獲取用戶資料失敗');
  } catch (error) {
    console.error('獲取用戶資料錯誤:', error);
    throw error;
  }
};

// 更新VIP狀態
export const updateVipStatus = async (days) => {
  try {
    const response = await authenticatedRequest('/profile/vip', {
      method: 'PUT',
      body: JSON.stringify({ days }),
    });
    
    if (response.ok) {
      return await response.json();
    }
    
    const errorData = await response.json();
    throw new Error(errorData.detail || '更新VIP狀態失敗');
  } catch (error) {
    console.error('更新VIP狀態錯誤:', error);
    throw error;
  }
};

// 檢查認證狀態
export const checkAuthStatus = () => {
  const token = getAuthToken();
  if (!token) {
    return false;
  }
  
  try {
    // 簡單檢查token格式（JWT）
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // 檢查是否過期
    if (payload.exp < currentTime) {
      removeAuthToken();
      return false;
    }
    
    return true;
  } catch (error) {
    removeAuthToken();
    return false;
  }
};

// 登出
export const logout = () => {
  removeAuthToken();
  window.location.href = '/signin';
};

// 註冊請求
export const registerUser = async (username, password, cfResponse) => {
  try {
    const response = await fetch(`${API_BASE_URL}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        cf_response: cfResponse,
      }),
    });

    const data = await response.json();
    
    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, error: data.detail || '註冊失敗' };
    }
  } catch (error) {
    console.error('註冊錯誤:', error);
    return { success: false, error: '網絡錯誤，請稍後重試' };
  }
};

// 登錄請求
export const loginUser = async (username, password, cfResponse) => {
  try {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        cf_response: cfResponse,
      }),
    });

    const data = await response.json();
    
    if (response.ok) {
      return { success: true, data };
    } else {
      let errorMessage = '登錄失敗';
      switch (response.status) {
        case 401:
          errorMessage = '用戶名或密碼錯誤';
          break;
        case 403:
          errorMessage = data.detail || '帳號已被禁止登錄';
          break;
        case 400:
          errorMessage = data.detail || '驗證失敗';
          break;
        default:
          errorMessage = data.detail || '登錄失敗，請稍後重試';
      }
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    console.error('登錄錯誤:', error);
    return { success: false, error: '網絡錯誤，請稍後重試' };
  }
};

// 修改密碼
export const changePassword = async (oldPassword, newPassword, cfResponse) => {
  try {
    const response = await authenticatedRequest(`/change-password`, {
      method: 'PUT',
      body: JSON.stringify({
        current_password: oldPassword,
        new_password: newPassword,
        cf_response: cfResponse,
      }),
    });
    
    if (response.ok) {
      return await response.json();
    }
    
    const errorData = await response.json();
    throw new Error(errorData.detail || '修改密碼失敗');
  } catch (error) {
    console.error('修改密碼錯誤:', error);
    throw error;
  }
};

export const getUserCommentHistory = async (page = 0, limit = 10) => {
  try {
    const response = await authenticatedRequest(`/user/comment-history?page=${page}&limit=${limit}`);
    
    if (!response) {
      // authenticatedRequest 已经处理了401错误并跳转
      return {
        items: [],
        pagination: {
          page: 0,
          limit: limit,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        }
      };
    }
    
    if (!response.ok) {
      if (response.status === 404) {
        // 如果API不存在，返回空的分頁數據而不是抛出错误
        console.warn('評論歷史API尚未實現');
        return {
          items: [],
          pagination: {
            page: 0,
            limit: limit,
            total_count: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false
          }
        };
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '獲取評論歷史失敗');
    }

    const data = await response.json();
    
    // 檢查返回的數據格式
    if (data && data.items && data.pagination) {
      // 如果是新的分頁格式，直接返回
      return data;
    } else if (Array.isArray(data)) {
      // 如果是舊格式（數組），轉換為分頁格式
      const startIndex = page * limit;
      const endIndex = startIndex + limit;
      const paginatedItems = data.slice(startIndex, endIndex);
      
      return {
        items: paginatedItems,
        pagination: {
          page: page,
          limit: limit,
          total_count: data.length,
          total_pages: Math.ceil(data.length / limit),
          has_next: endIndex < data.length,
          has_prev: page > 0
        }
      };
    } else {
      // 如果格式不正確，返回空的分頁數據
      console.warn('API返回數據格式不正確');
      return {
        items: [],
        pagination: {
          page: 0,
          limit: limit,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        }
      };
    }
  } catch (error) {
    // 如果是网络错误或解析错误，返回空的分頁數據而不是抛出错误
    console.error('獲取評論歷史錯誤:', error);
    if (error.message.includes('Unexpected token') || error.message.includes('not valid JSON')) {
      console.warn('API端點可能尚未實現，返回空數據');
    }
    // 对于其他网络错误，也返回空的分頁數據，避免界面崩溃
    return {
      items: [],
      pagination: {
        page: page,
        limit: limit,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      }
    };
  }
};

// 獲取今日熱點（公開接口，不需要登入）
export const getHotTopics = async (signal) => {
  const response = await fetch(`${API_BASE_URL}/hot-topics`, { signal });
  if (!response.ok) {
    throw new Error('獲取今日熱點失敗');
  }
  return await response.json();
};